// src/redux/slices/cartSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import API from "../util/API";
import { logout as logoutUser } from "./userAuthSlice";
import { readScopedJson, removeScopedStorage, writeScopedJson } from "../util/privacyConsent";

const DEFAULT_IMAGE_URL = "/placeholder.svg";
export const MAX_CART_ITEM_QUANTITY = 12;

const isNonEmptyString = (value) => typeof value === "string" && value.trim().length > 0;

const readMinCartItemQuantity = (item) => {
  const n = Number(item?.min_order_quantity ?? item?.minOrderQuantity ?? item?.sku_bulk_order ?? 1);
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.min(MAX_CART_ITEM_QUANTITY, Math.floor(n)));
};

const clampCartItemQuantity = (value, fallback = 1, minimum = 1) => {
  const n = Number(value);
  const base = Number.isFinite(n) ? Math.trunc(n) : fallback;
  const min = Math.max(1, Math.min(MAX_CART_ITEM_QUANTITY, Math.floor(Number(minimum) || 1)));
  return Math.max(min, Math.min(MAX_CART_ITEM_QUANTITY, base));
};

const resolveImageUrl = (value, fallback = DEFAULT_IMAGE_URL) => {
  if (isNonEmptyString(value)) return value.trim();
  if (Array.isArray(value) && value.length) return resolveImageUrl(value[0], fallback);
  if (value && typeof value === "object") {
    return resolveImageUrl(
      value.url ||
        value.imageUrl ||
        value.src ||
        value.href ||
        value.link ||
        value.thumbnail ||
        value.thumb ||
        value.small ||
        value.medium ||
        value.large ||
        value.original ||
        value.sku_image,
      fallback
    );
  }
  return fallback;
};

const ensureCartItemId = (incoming) => {
  const raw = incoming && typeof incoming === "object" ? { ...incoming } : {};
  const currentId = String(raw.id || "").trim();
  if (currentId) return { ...raw, id: currentId };

  const productId = String(raw.product_id || raw.productId || "").trim();
  const skuId = String(raw.sku_id || raw.skuId || "").trim();

  if (productId && skuId) return { ...raw, id: `${productId}:${skuId}` };
  if (productId) return { ...raw, id: productId };

  return {
    ...raw,
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
  };
};

const normalizeCartItem = (incoming) => {
  const normalized = ensureCartItemId(incoming);
  const picked = resolveImageUrl(
    normalized.image || normalized.imageUrl || normalized.images,
    DEFAULT_IMAGE_URL
  );
  normalized.image = picked;
  if (!isNonEmptyString(normalized.imageUrl)) normalized.imageUrl = picked;
  const minimum = readMinCartItemQuantity(normalized);
  if (minimum > 1) normalized.min_order_quantity = minimum;
  normalized.quantity = clampCartItemQuantity(normalized.quantity ?? normalized.qty ?? minimum, minimum, minimum);
  return normalized;
};

// Load cart from privacy-scoped storage.
const loadCart = () => {
  try {
    const parsed = readScopedJson("cart", [], { persistWhenAccepted: true });
    if (!Array.isArray(parsed)) return [];
    return parsed.map((it) => normalizeCartItem(it));
  } catch {
    return [];
  }
};

// Save cart to privacy-scoped storage.
const saveCart = (cart) => {
  try {
    writeScopedJson("cart", cart, { persistWhenAccepted: true });
  } catch {
    // Ignore storage errors silently
  }
};

export const CHECKOUT_SELECTION_KEY = "cherrfy_checkout_selection_v1";
const savedCheckout = readScopedJson(CHECKOUT_SELECTION_KEY, null, { persistWhenAccepted: false });
const persistCheckoutSelection = (state) => {
  if (!Array.isArray(state.checkoutItems) || !state.checkoutItems.length) {
    removeScopedStorage(CHECKOUT_SELECTION_KEY);
    return;
  }
  writeScopedJson(CHECKOUT_SELECTION_KEY, { items: state.checkoutItems, summary: state.checkoutSummary }, { persistWhenAccepted: false });
};

const initialState = {
  items: loadCart(),
  checkoutItems: Array.isArray(savedCheckout?.items) ? savedCheckout.items.map(normalizeCartItem) : [],
  checkoutSummary: savedCheckout?.summary || null,
  lastAddNotice: null,
  serverSync: {
    status: "idle",
    error: null,
    lastPulledAt: null,
    lastPushedAt: null,
  },
};

const mergeCartItems = (serverItems, localItems) => {
  const byId = new Map();
  const add = (it, source) => {
    if (!it || typeof it !== "object") return;
    const withId = ensureCartItemId(it);
    const id = String(withId.id || "").trim();
    if (!id) return;
    if (!byId.has(id)) {
      byId.set(id, normalizeCartItem({ ...withId, id }));
      return;
    }
    const prev = byId.get(id);
    const merged = {
      ...prev,
      ...withId,
      id,
    };
    const minimum = Math.max(readMinCartItemQuantity(prev), readMinCartItemQuantity(withId));
    if (minimum > 1) merged.min_order_quantity = minimum;
    merged.quantity = clampCartItemQuantity(
      Math.max(Number(prev.quantity || 0), Number(it.quantity || 0), minimum),
      minimum,
      minimum
    );
    // Prefer local for user-selected fields.
    if (source === "local") {
      merged.selected_shipping = it.selected_shipping ?? prev.selected_shipping;
      merged.selected_shipping_meta = it.selected_shipping_meta ?? prev.selected_shipping_meta;
      merged.logistics_service_name = it.logistics_service_name ?? prev.logistics_service_name;
    }
    byId.set(id, merged);
  };

  (Array.isArray(serverItems) ? serverItems : []).forEach((it) => add(it, "server"));
  (Array.isArray(localItems) ? localItems : []).forEach((it) => add(it, "local"));
  return Array.from(byId.values());
};

export const fetchServerCart = createAsyncThunk(
  "cart/fetchServerCart",
  async (_, { rejectWithValue }) => {
    try {
      const res = await API.get("/cart");
      return res.data?.data ?? res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const saveServerCart = createAsyncThunk(
  "cart/saveServerCart",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await API.put("/cart", payload);
      return res.data?.data ?? res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

export const syncCartAfterLogin = createAsyncThunk(
  "cart/syncCartAfterLogin",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const localItems = state?.cart?.items || [];

      const server = await dispatch(fetchServerCart()).unwrap();
      const serverItems = Array.isArray(server?.items) ? server.items : [];

      if (serverItems.length === 0 && Array.isArray(localItems) && localItems.length > 0) {
        await dispatch(saveServerCart({ items: localItems })).unwrap();
        return { mode: "pushedLocal", items: localItems };
      }

      if (serverItems.length > 0) {
        const merged = mergeCartItems(serverItems, localItems);
        dispatch(replaceCart(merged));
        // Also push merged back so server stays consistent.
        await dispatch(saveServerCart({ items: merged })).unwrap();
        return { mode: "pulledServer", items: merged };
      }

      return { mode: "noop", items: localItems };
    } catch (err) {
      return rejectWithValue(String(err?.message || err));
    }
  }
);

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    replaceCart: (state, action) => {
      const next = Array.isArray(action.payload) ? action.payload.map(normalizeCartItem) : [];
      state.items = next;
      saveCart(state.items);
    },
    addToCart: (state, action) => {
      const incoming = action.payload;
      const key =
        incoming?.id ||
        (incoming?.product_id && incoming?.sku_id
          ? `${incoming.product_id}:${incoming.sku_id}`
          : incoming?.product_id
            ? String(incoming.product_id)
            : `${Date.now()}-${Math.random().toString(16).slice(2)}`);

      const item = normalizeCartItem({ ...incoming, id: key });
      const existing = state.items.find((i) => i.id === item.id);
      if (existing) {
        const minimum = Math.max(readMinCartItemQuantity(existing), readMinCartItemQuantity(item));
        if (minimum > 1) existing.min_order_quantity = minimum;
        const requested = existing.quantity + (item.quantity || minimum);
        const next = clampCartItemQuantity(requested, minimum, minimum);
        if (next < requested) {
          state.lastAddNotice = `Only ${next} of this item can be in the cart (limit ${MAX_CART_ITEM_QUANTITY}).`;
        }
        existing.quantity = next;

        // Keep image up-to-date if the earlier item was missing/placeholder.
        const existingImg = resolveImageUrl(existing.image, DEFAULT_IMAGE_URL);
        const incomingImg = resolveImageUrl(item.image, DEFAULT_IMAGE_URL);
        if (!isNonEmptyString(existing.image) || existingImg === DEFAULT_IMAGE_URL) {
          existing.image = incomingImg;
        }
        if (!isNonEmptyString(existing.imageUrl) || existing.imageUrl === DEFAULT_IMAGE_URL) {
          existing.imageUrl = incomingImg;
        }
      } else {
        const minimum = readMinCartItemQuantity(item);
        state.items.push({ ...item, quantity: clampCartItemQuantity(item.quantity || minimum, minimum, minimum) });
      }
      saveCart(state.items);
    },
    checkout: (state, action) => {
      const payload = action.payload;

      // Backward compatible: allow passing an array directly.
      if (payload && typeof payload === "object" && !Array.isArray(payload) && Array.isArray(payload.items)) {
        state.checkoutItems = payload.items.map(normalizeCartItem);
        state.checkoutSummary = payload.summary || null;
      } else {
        state.checkoutItems = Array.isArray(payload) ? payload.map(normalizeCartItem) : payload;
        state.checkoutSummary = null;
      }
      persistCheckoutSelection(state);
    },
    updateCheckoutSummary: (state, action) => {
      state.checkoutSummary = {
        ...(state.checkoutSummary || {}),
        ...(action.payload || {}),
      };
      persistCheckoutSelection(state);
    },
    removeFromCart: (state, action) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
      saveCart(state.items);
    },
    removeManyFromCart: (state, action) => {
      const ids = Array.isArray(action.payload) ? action.payload : [];
      const set = new Set(ids.filter(Boolean).map(String));
      if (!set.size) return;
      state.items = state.items.filter((item) => !set.has(String(item.id)));
      saveCart(state.items);
    },
    clearCart: (state) => {
      state.items = [];
      saveCart(state.items);
    },
    updateQuantity: (state, action) => {
      const { id, quantity } = action.payload;
      const item = state.items.find((item) => item.id === id);
      if (item && quantity > 0) {
        const minimum = readMinCartItemQuantity(item);
        item.quantity = clampCartItemQuantity(quantity, item.quantity || minimum, minimum);
      }
      saveCart(state.items);
    },

    updateCartItem: (state, action) => {
      const { id, patch } = action.payload || {};
      if (!id || !patch || typeof patch !== "object") return;
      const item = state.items.find((it) => it.id === id);
      if (!item) return;

      Object.assign(item, patch);
      if (Object.prototype.hasOwnProperty.call(patch, "quantity")) {
        const minimum = readMinCartItemQuantity(item);
        item.quantity = clampCartItemQuantity(patch.quantity, item.quantity || minimum, minimum);
      }
      saveCart(state.items);
    },
    clearAddNotice: (state) => {
      state.lastAddNotice = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchServerCart.pending, (state) => {
        state.serverSync.status = "loading";
        state.serverSync.error = null;
      })
      .addCase(fetchServerCart.fulfilled, (state) => {
        state.serverSync.status = "succeeded";
        state.serverSync.lastPulledAt = Date.now();
      })
      .addCase(fetchServerCart.rejected, (state, action) => {
        state.serverSync.status = "failed";
        state.serverSync.error = action.payload || action.error?.message || "Fetch cart failed";
      })
      .addCase(saveServerCart.fulfilled, (state) => {
        state.serverSync.lastPushedAt = Date.now();
      })
      .addCase(logoutUser, (state) => {
        // Explicit logout should clear visible/local cart.
        state.items = [];
        state.checkoutItems = [];
        state.checkoutSummary = null;
        removeScopedStorage(CHECKOUT_SELECTION_KEY);
        state.serverSync.status = "idle";
        state.serverSync.error = null;
        saveCart(state.items);
      });
  },
});

export const { replaceCart, addToCart, removeFromCart, removeManyFromCart, clearCart, updateQuantity, updateCartItem, checkout, updateCheckoutSummary, hydrateCartFromStorage, clearAddNotice } =
  cartSlice.actions;
export default cartSlice.reducer;
